import React from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@design-system/Sheet.tsx';
import { MyGoalsIcon } from '@/components/icons/MyGoalsIcon';
import { MyHealthScoresIcon } from '@/components/icons/MyHealthScoresIcon';
import { MyHealthConnectionIcon } from '@/components/icons/MyHealthConnectionIcon';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';

interface FormulaBenefitsSheetProps {
  productId: number;
  formulaBenefitsData:
    | {
        goals: string[];
        scores: { title: string; ranking: string }[];
        connections: { title: string; no_of_connections: number }[];
      }
    | undefined;
}

export const FormulaBenefitsSheet: React.FC<FormulaBenefitsSheetProps> = ({
  productId,
  formulaBenefitsData,
}) => {
  const hasGoals = (formulaBenefitsData?.goals?.length ?? 0) > 0;
  const hasScores = (formulaBenefitsData?.scores?.length ?? 0) > 0;

  const handleTrackEvent = () => {
    if (productId === 1) {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_ORALLOZENGES_MYFORMULABENEFITS,
      );
    } else if (productId === 2) {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_SUPPLEMENTS_MYFORMULABENEFITS,
      );
    } else if (productId === 3) {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_GUTFORMULA_MYFORMULABENEFITS,
      );
    } else if (productId === 4) {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TOOTHPASTEANDGEL_MYFORMULABENEFITS,
      );
    }
  };

  return (
    <Sheet>
      <SheetTrigger onClick={handleTrackEvent}>
        <button
          data-product={productId}
          className="flex items-center rounded-md pl-4 pr-2 py-3 typography-btn-medium text-secondary justify-center transition-colors bg-gradient-to-b data-[product=2]:from-[#EBFCEE] data-[product=2]:to-[#EBFCEE] data-[product=3]:from-[#FCF9E3] data-[product=3]:to-[#FCF9E3] data-[product=1]:from-[#F0EBFF] data-[product=1]:to-[#F0EBFF] from-[#EAEFFC] to-[#EAEFFC]"
        >
          My Formula Benefits{' '}
          <ChevronRight className="text-secondary-foreground h-4 w-4 mt-0.5 ml-1" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full md:w-[400px] bg-white">
        <SheetHeader className="p-4 mb-4">
          <br />
          <br />
          <p className="typography-h3 text-primary">
            Here's where your personalized formula will focus:
          </p>
        </SheetHeader>
        <div className="p-4 space-y-6 overflow-y-auto">
          {formulaBenefitsData?.goals &&
            formulaBenefitsData.goals.length > 0 && (
              <div>
                <div className="flex items-center mb-6">
                  <MyGoalsIcon width={24} height={24} />
                  <h3 className="typography-h3 text-primary ml-2">My Goals</h3>
                </div>
                <hr className="border-secondary border-[1.5px]" />
                <ul className="space-y-1">
                  {formulaBenefitsData.goals.map(
                    (goal: string, index: number) => (
                      <li
                        key={index}
                        className="typography-title text-secondary py-4 border-b border-secondary"
                      >
                        {goal}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
          {formulaBenefitsData?.scores &&
            formulaBenefitsData.scores.length > 0 && (
              <div className={hasGoals ? '!mt-12' : ''}>
                <div className="flex items-center mb-6">
                  <MyHealthScoresIcon width={24} height={24} />
                  <h3 className="typography-h3 text-primary ml-2">
                    My Health Scores
                  </h3>
                </div>
                <hr className="border-secondary border-[1.5px]" />
                <ul className="space-y-1">
                  {formulaBenefitsData.scores.map(
                    (
                      score: { title: string; ranking: string },
                      index: number,
                    ) => (
                      <li
                        key={index}
                        className="py-4 border-b border-secondary"
                      >
                        <p className="typography-title text-secondary">
                          {score.title}
                        </p>
                        <p className="typography-btn-medium text-tertiary">
                          {score.ranking}
                        </p>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
          {formulaBenefitsData?.connections &&
            formulaBenefitsData.connections.length > 0 && (
              <div className={hasGoals || hasScores ? '!mt-12' : ''}>
                <div className="flex items-center mb-6">
                  <MyHealthConnectionIcon width={24} height={24} />
                  <h3 className="typography-h3 text-primary ml-2">
                    My Health Connections
                  </h3>
                </div>
                <hr className="border-secondary border-[1.5px]" />
                <ul className="space-y-1">
                  {formulaBenefitsData.connections.map(
                    (
                      connection: {
                        title: string;
                        no_of_connections: number;
                      },
                      index: number,
                    ) => (
                      <li
                        key={index}
                        className="py-4 border-b border-secondary"
                      >
                        <p className="typography-title text-secondary">
                          {connection.title}
                        </p>
                        <p className="typography-btn-medium text-tertiary">
                          {connection.no_of_connections} connections
                        </p>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
